import Typography from "@mui/material/Typography";

import useIntegrationSettings from "@app/analytics/hooks/integrations/useIntegrationSettings";
import LastPushState from "@app/analytics/Integrations/components/LastPushState";
import SettingSwitch, {
  SettingSwitchProps,
} from "@app/analytics/Integrations/components/SettingSwitch";
import { toast } from "@app/common/hooks/useToasts";
import type {
  Integration,
  HubSpotPushIntegrationSetting,
  UpdateIntegrationSettingRequestBody,
  UserIntegrationSetting,
} from "@app/common/types";

import { Column } from "@rui/Stack";

/** unique key used only for React keys */
type SettingSwitch = SettingSwitchProps & { key: string };

interface ConnectedContentProps {
  integration: Integration;
}

const getSettingSwitches = (
  integrationSettings: Integration["integrationSettings"],
  updateIntegrationSetting: (body: UpdateIntegrationSettingRequestBody) => void
): SettingSwitch[] => {
  const push = integrationSettings.byId
    .hubspot_push as HubSpotPushIntegrationSetting;
  const pull = integrationSettings.byId.hubspot_pull;

  return [
    {
      key: "push-to-hubspot",
      title: "Auto-Push Meeting Notes to HubSpot",
      description:
        "Automatically update HubSpot with meeting notes and participant information",
      checked: Boolean(push?.enabled),
      onChange: (_event, checked) =>
        updateIntegrationSetting({
          integrationKind: "hubspot_push",
          enabled: checked,
          providerSpecificSettings: push?.providerSpecificSettings,
        }),
    },
    {
      key: "pull-to-read",
      title: "Incorporate HubSpot data into Read",
      description:
        "Allow Read to access your HubSpot data, including contacts and leads",
      checked: Boolean(pull?.enabled),
      onChange: (_event, checked) =>
        updateIntegrationSetting({
          integrationKind: "hubspot_pull",
          enabled: checked,
        }),
    },
    {
      key: "create-contacts",
      title: "Create new HubSpot contacts*",
      description:
        "Automatically create a new HubSpot contact for meeting participants if no existing contact is found*",
      checked: Boolean(push?.providerSpecificSettings.createNewContacts),
      onChange: (_event, checked) =>
        updateIntegrationSetting({
          integrationKind: "hubspot_push",
          enabled: Boolean(push?.enabled),
          providerSpecificSettings: {
            ...push?.providerSpecificSettings,
            createNewContacts: checked,
          },
        }),
    },
    {
      key: "include-transcript",
      title: "Include Meeting Transcript*",
      description: "Attach transcript files to your meeting notes in HubSpot",
      checked: Boolean(push?.providerSpecificSettings.includeTranscript),
      onChange: (_event, checked) =>
        updateIntegrationSetting({
          integrationKind: "hubspot_push",
          enabled: Boolean(push?.enabled),
          providerSpecificSettings: {
            ...push?.providerSpecificSettings,
            includeTranscript: checked,
          },
        }),
    },
  ];
};

const ConnectedContent = ({
  integration: { integrationSettings },
}: ConnectedContentProps) => {
  const { updateIntegrationSetting } = useIntegrationSettings();

  const handleUpdateIntegrationSetting = (
    body: UpdateIntegrationSettingRequestBody
  ) => {
    updateIntegrationSetting.mutate(body, {
      onError: () => {
        toast.error(
          "Something went wrong and your changes could not be saved. Please try again later or contact support@read.ai if the problem persists."
        );
      },
    });
  };

  const settingSwitches = getSettingSwitches(
    integrationSettings,
    handleUpdateIntegrationSetting
  );

  const connectionHealth = (
    integrationSettings.byId.hubspot_push as UserIntegrationSetting | undefined
  )?.connectionHealth;

  return (
    <>
      <Typography variant="bodyPrimaryMed" color="shades.neutral.700">
        Settings
      </Typography>
      <Column spacing={2}>
        {settingSwitches.map(({ key, ...setting }) => (
          <SettingSwitch key={key} {...setting} />
        ))}
      </Column>
      <Typography variant="labelReg" color="shades.neutral.500">
        *Even if you've disabled these settings, they might still be active if
        another Read user connected to your HubSpot account has enabled them.
      </Typography>
      <LastPushState
        date={connectionHealth?.lastSyncTime}
        syncState={connectionHealth?.syncState}
      />
    </>
  );
};

export default ConnectedContent;
